/**
 * SenderFooter — Sender 底部栏（Sources 范围、字数统计、Sources 按钮、发送/停止按钮）
 * 从 CustomerSuggestion 中抽取
 */
import { ArrowRightOutlined } from "@ant-design/icons";
import { Button, Checkbox, Flex, Space, Tooltip, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import WenjianshouIcon from "@/assets/opsmate/Sources.svg";
import { MAX_QUESTION_LENGTH } from "@/hooks/opsmate/useSubmitMessage";
import KBScopeSelector from "@/pages/opsmate/components/CustomerSuggestion/KBScopeSelector";
import type {
  KBScope,
  SourceTypeOption,
  StudyOption,
} from "@/pages/opsmate/components/CustomerSuggestion/KBScopeSelector";

interface SenderFooterProps {
  value: string;
  submitting: boolean;
  isInitPage: boolean;
  kbScopes: KBScope[];
  onScopesChange: (scopes: KBScope[]) => void;
  visibleScopes: KBScope[];
  sourceTypes: SourceTypeOption[];
  selectedSourceTypes: string[] | null;
  onSourceTypesChange: (typeIds: string[] | null) => void;
  availableStudies: StudyOption[];
  selectedStudies: string[] | null;
  onStudiesChange: (studyIds: string[] | null) => void;
  showSourcesCount: boolean;
  selectedSourcesCount: number;
  availableSourcesCount: number;
  hasSelectedSources: boolean;
  onlyUseSelectedSources: boolean;
  onOnlyUseSelectedSourcesChange: (checked: boolean) => void;
  setDocsCollapsed: (v: boolean) => void;
  onSubmit: () => void;
  onStopGeneration: () => void;
}

export default function SenderFooter({
  value,
  submitting,
  isInitPage,
  kbScopes,
  onScopesChange,
  visibleScopes,
  sourceTypes,
  selectedSourceTypes,
  onSourceTypesChange,
  availableStudies,
  selectedStudies,
  onStudiesChange,
  showSourcesCount,
  selectedSourcesCount,
  availableSourcesCount,
  hasSelectedSources,
  onlyUseSelectedSources,
  onOnlyUseSelectedSourcesChange,
  setDocsCollapsed,
  onSubmit,
  onStopGeneration,
}: SenderFooterProps) {
  const { t } = useTranslation();

  const isEmpty = value.trim().length === 0;
  const onlySelectedAndEmpty = !!onlyUseSelectedSources && !hasSelectedSources;
  const disabled =
    ((isEmpty || onlySelectedAndEmpty) && !submitting) || value.length > MAX_QUESTION_LENGTH;

  const [stopTooltipReady, setStopTooltipReady] = useState(false);
  const [showOnlySelectedTooltip, setShowOnlySelectedTooltip] = useState(false);
  useEffect(() => {
    if (!submitting) {
      setStopTooltipReady(false);
      return;
    }
    const timer = setTimeout(() => setStopTooltipReady(true), 2000);
    return () => clearTimeout(timer);
  }, [submitting]);
  function RectIcon() {
    return <div style={{ width: 12, height: 12, background: "#fff", borderRadius: 4 }}></div>;
  }
  const sendBtn = submitting ? (
    <Tooltip
      title={t("opsmate.suggestion.stopGenerating")}
      trigger="hover"
      open={stopTooltipReady ? undefined : false}
    >
      <Button
        onClick={onStopGeneration}
        icon={<RectIcon />}
        className="opsmate-send-btn"
        shape="circle"
        type="primary"
      />
    </Tooltip>
  ) : (
    <Button
      onClick={onSubmit}
      icon={<ArrowRightOutlined style={{ fontSize: 16, color: "#fff" }} />}
      className="opsmate-send-btn"
      disabled={disabled}
      shape="circle"
      type="primary"
    />
  );

  // KBScopeSelector 与「仅使用已选 Sources」勾选框互斥，二者不能同时选中。
  const handleScopesChange = (scopes: KBScope[]) => {
    if (scopes.length > 0 && onlyUseSelectedSources) onOnlyUseSelectedSourcesChange(false);
    onScopesChange(scopes);
  };
  const handleSourceTypesChange = (typeIds: string[] | null) => {
    if (typeIds !== null && onlyUseSelectedSources) onOnlyUseSelectedSourcesChange(false);
    onSourceTypesChange(typeIds);
  };
  const handleStudiesChange = (studyIds: string[] | null) => {
    if (studyIds !== null && onlyUseSelectedSources) onOnlyUseSelectedSourcesChange(false);
    onStudiesChange(studyIds);
  };
  const handleOnlyUseSelectedSourcesChange = (checked: boolean) => {
    if (checked && kbScopes.length > 0) onScopesChange([]);
    onOnlyUseSelectedSourcesChange(checked);
  };

  const leftControlStyle = undefined;
  const sourceBtnStyle = isInitPage
    ? {
        border: "none",
        background: "transparent",
        boxShadow: "none",
        padding: 0,
        visibility: "hidden" as const,
        pointerEvents: "none" as const,
      }
    : { border: "none", background: "transparent", boxShadow: "none", padding: 0 };

  const sourcesCountValue = showSourcesCount ? selectedSourcesCount : 0;

  return (
    <Flex justify="space-between" align="center" style={{ marginTop: 4 }}>
      <Space size={8} align="center" style={leftControlStyle}>
        <KBScopeSelector
          value={kbScopes}
          onChange={handleScopesChange}
          size="sm"
          visibleScopes={visibleScopes}
          sourceTypes={sourceTypes}
          selectedTypes={selectedSourceTypes}
          onTypesChange={handleSourceTypesChange}
          availableStudies={availableStudies}
          selectedStudies={selectedStudies}
          onStudiesChange={handleStudiesChange}
        />
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 2 }}>
          <Checkbox
            disabled={availableSourcesCount === 0}
            checked={!!onlyUseSelectedSources}
            onChange={(e) => handleOnlyUseSelectedSourcesChange(e.target.checked)}
            styles={{ root: { alignItems: "center" }, label: { paddingInlineEnd: 2 } }}
          >
            <Typography.Text
              style={{
                height: 18,
                fontWeight: 400,
                fontSize: 12,
                color: "#830051",
                lineHeight: "18px",
                textAlign: "left",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={() => setShowOnlySelectedTooltip(true)}
              onMouseLeave={() => setShowOnlySelectedTooltip(false)}
            >
              {t("opsmate.suggestion.onlySelectedSources")}
            </Typography.Text>
          </Checkbox>
          <span
            style={{
              display: "inline-flex",
              width: 16,
              height: 16,
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
            onMouseEnter={() => setShowOnlySelectedTooltip(true)}
            onMouseLeave={() => setShowOnlySelectedTooltip(false)}
          >
            <i
              className="ri-question-line"
              style={{ display: "block", fontSize: 12, color: "#830051", lineHeight: 1 }}
            />
          </span>
          {showOnlySelectedTooltip ? (
            <div
              style={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                marginBottom: 8,
                width: 256,
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 12,
                lineHeight: "18px",
                zIndex: 50,
                backgroundColor: "#fff",
                border: "1px solid #f0e6ec",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Typography.Text style={{ color: "#6b7280", fontSize: 12 }}>
                {t("opsmate.suggestion.onlySelectedSourcesTooltip")}
              </Typography.Text>
            </div>
          ) : null}
        </div>
      </Space>
      <Flex align="center" gap={8}>
        <Typography.Text
          style={{
            fontSize: 12,
            color: value.length > MAX_QUESTION_LENGTH ? "#ff4d4f" : "rgba(0,0,0,0.25)",
            whiteSpace: "nowrap",
          }}
        >
          {value.length} / {MAX_QUESTION_LENGTH}
        </Typography.Text>
        {isInitPage ? (
          <div />
        ) : (
          <div style={{ position: "relative", display: "inline-flex" }}>
            <Button
              type="text"
              aria-label="Sources"
              icon={<img src={WenjianshouIcon} alt="" aria-hidden />}
              onClick={() => {
                if (typeof setDocsCollapsed === "function") setDocsCollapsed(false);
              }}
              size="small"
              style={sourceBtnStyle}
            />
            {sourcesCountValue > 0 ? (
              <span
                style={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  width: 20,
                  height: 14,
                  background: "#830051",
                  borderRadius: 7,
                  border: "1px solid #F8F2F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                  padding: 0,
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 10,
                    color: "#FFFFFF",
                    lineHeight: "10px",
                    textAlign: "center",
                  }}
                >
                  {sourcesCountValue > 99 ? "99+" : String(sourcesCountValue)}
                </span>
              </span>
            ) : null}
          </div>
        )}
        {(isEmpty || onlySelectedAndEmpty) && !submitting ? (
          <Tooltip
            title={
              onlySelectedAndEmpty
                ? t("opsmate.suggestion.emptySelectedSourcesTooltip")
                : t("opsmate.suggestion.inputTooltip")
            }
            placement="top"
            arrow={{ pointAtCenter: true }}
            color="#fff"
            styles={{
              container: {
                color: "rgba(0,0,0,0.85)",
                borderRadius: 8,
                boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                padding: "8px 12px",
                fontSize: 14,
              },
            }}
          >
            <span style={{ display: "inline-flex" }}>{sendBtn}</span>
          </Tooltip>
        ) : (
          sendBtn
        )}
      </Flex>
    </Flex>
  );
}
